import { useCallback } from "react"
import { keys } from "lodash"
import { Box, Typography } from "@mui/material"
import { useFormContext } from "connected-components/optimize-experiments/form-context"
import { RULES_CONSTRAINTS } from "connected-components/optimize-experiments/constants"
import ConstraintSwitch from "connected-components/optimize-experiments/components/constraint-switch"
import { FORM_CONSTRAINTS } from "connected-components/optimize-experiments/configuration-sections"
import { LONG_SHORT_PORTFOLIO_TYPE, PortfolioType } from "connected-components/optimize-experiments/components/helpers/configuration"
import { concentrationSchema, equityChangeSchema, maxTradeSchema, maxTurnoverSchema, netLongSchema } from "connected-components/optimize-experiments/form-validation-schemas/rules"

import "./style.scss"

interface RulesProps {
  portfolioType: PortfolioType
  portfolioNetLong: number
}

function getTypedKeys<T extends object>(obj: T): (keyof T)[] {
  return keys(obj) as (keyof T)[]
}

const CONSTRAINT_KEYS = getTypedKeys(FORM_CONSTRAINTS)

export default function Rules({ portfolioType, portfolioNetLong }: RulesProps) {
  const form = useFormContext()
  const getConstraintConfig = useCallback((constraintKey: keyof typeof FORM_CONSTRAINTS) => {
    const baseConfig = FORM_CONSTRAINTS[constraintKey]

    if (constraintKey === RULES_CONSTRAINTS.NET_LONG) {
      return {
        ...baseConfig,
        defaultValue: {
          longMarketValue: (portfolioNetLong * 100).toFixed(2),
          shortMarketValue: parseFloat((100 - portfolioNetLong * 100).toFixed(2)),
        },
      }
    }
    return baseConfig
  }, [portfolioNetLong])

  const getConstraintProps = useCallback((constraintKey: keyof typeof FORM_CONSTRAINTS) => {
    if (constraintKey === RULES_CONSTRAINTS.NET_LONG) {
      return { disabled: portfolioType !== LONG_SHORT_PORTFOLIO_TYPE }
    }
    return {}
  }, [portfolioType])

  const getValidator = (constraintKey: keyof typeof FORM_CONSTRAINTS) => {
    switch (constraintKey) {
      case RULES_CONSTRAINTS.MAX_TRADE:
        return maxTradeSchema
      case RULES_CONSTRAINTS.NET_LONG:
        return netLongSchema
      case RULES_CONSTRAINTS.CONCENTRATION:
        return concentrationSchema
      case RULES_CONSTRAINTS.MAX_TURNOVER:
        return maxTurnoverSchema
      case RULES_CONSTRAINTS.EQUITY_CHANGE:
        return equityChangeSchema
      default:
        return undefined
    }
  }

  return (
    <Box className="components_rules">
      <Typography color="inherit" noWrap className="rules-label">
        Rules
      </Typography>
      <Typography color="secondary" variant="caption">
        Set constraints to control risk, exposure, turnover, and compliance requirements.
      </Typography>
      <Box className="rules-constraint">
        {CONSTRAINT_KEYS.map((constraintKey) => {
          const validator = getValidator(constraintKey)
          return (
            <form.Field
              key={constraintKey}
              name={`rules.${constraintKey}`}
              validators={validator ? { onChange: validator } : undefined}
            >
              {(field) => (
                <ConstraintSwitch
                  field={field}
                  config={getConstraintConfig(constraintKey)}
                  {...getConstraintProps(constraintKey)}
                />
              )}
            </form.Field>
          )
        })}
      </Box>
    </Box>
  )
}
